import { jobWindow } from "@/lib/instructionTags";

const toRad = (d) => (d * Math.PI) / 180;
export const distanceMi = (a, b) => {
  const R = 3958.8, dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const fmtHour = (h) => (h === 24 ? "midnight" : h === 12 ? "noon" : h > 12 ? `${h - 12} pm` : `${h} am`);

/** Nearest-neighbour + 2-opt within time buckets. Time-window tags (before 8 am, morning, mid-day, evening, after 5 pm) are honoured:
 *  jobs whose window is still ahead today are kept in order of window start; jobs whose window has already closed, or "no Sunday service" on a Sunday, are deferred. */
export function planRoute(jobs, start, now = new Date()) {
  const hour = now.getHours() + now.getMinutes() / 60;
  const sunday = now.getDay() === 0;
  const deferred = [], skipped = [];
  const buckets = new Map(); // key: window start hour (or -1 = anytime)
  for (const j of jobs) {
    if (j.recipient_lat == null || j.recipient_lng == null) { skipped.push(j); continue; }
    if (sunday && (j.instruction_tags || []).includes("no_sunday")) { deferred.push(j); continue; }
    const w = jobWindow(j);
    if (w && hour >= w[1]) { deferred.push(j); continue; }
    const key = w ? w[0] : -1;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push({ job: j, lat: j.recipient_lat, lng: j.recipient_lng, window: w });
  }
  // Anytime jobs fill the first bucket whose window is already open; if none is open yet they go first on their own
  const keys = [...buckets.keys()].filter((k) => k !== -1).sort((a, b) => a - b);
  const anytime = buckets.get(-1) || [];
  const groups = keys.map((k) => buckets.get(k));
  if (keys.length && keys[0] <= hour) groups[0] = [...anytime, ...groups[0]];
  else groups.unshift(anytime);
  let cur = start || null; const order = [];
  for (const g of groups) {
    const seq = nnTwoOpt(g, cur);
    order.push(...seq);
    if (seq.length) cur = seq[seq.length - 1];
  }
  const result = order.map((p, i) => ({ job: p.job, legMiles: i === 0 ? (start ? distanceMi(start, p) : 0) : distanceMi(order[i - 1], p),
    windowNote: p.window ? (hour < p.window[0] ? `not before ${fmtHour(p.window[0])}` : `before ${fmtHour(p.window[1])}`) : null }));
  return { order: result, skipped: skipped.length, deferred: deferred.map((j) => ({ id: j.id, job_number: j.job_number })), sunday, totalMiles: result.reduce((s, r) => s + r.legMiles, 0) };
}

function nnTwoOpt(pts, start) {
  if (!pts.length) return [];
  let cur = start || pts[0];
  const remaining = [...pts]; const order = [];
  while (remaining.length) {
    let bi = 0, bd = Infinity;
    remaining.forEach((p, i) => { const d = distanceMi(cur, p); if (d < bd) { bd = d; bi = i; } });
    cur = remaining.splice(bi, 1)[0]; order.push(cur);
  }
  const cost = (arr) => arr.reduce((s, p, i) => s + distanceMi(i === 0 ? (start || p) : arr[i - 1], p), 0);
  let improved = true;
  while (improved && order.length > 3) {
    improved = false;
    for (let i = 0; i < order.length - 1; i++) for (let k = i + 1; k < order.length; k++) {
      const cand = [...order.slice(0, i), ...order.slice(i, k + 1).reverse(), ...order.slice(k + 1)];
      if (cost(cand) + 1e-6 < cost(order)) { order.splice(0, order.length, ...cand); improved = true; }
    }
  }
  return order;
}

/** Google Maps directions link with up to 9 waypoints (Google's URL limit). */
export const googleRouteUrl = (start, stops) => {
  const enc = (p) => `${p.lat},${p.lng}`;
  const s = stops.slice(0, 10);
  const dest = s[s.length - 1]; const way = s.slice(0, -1).map(enc).join("|");
  return `https://www.google.com/maps/dir/?api=1${start ? `&origin=${enc(start)}` : ""}&destination=${enc(dest)}${way ? `&waypoints=${way}` : ""}&travelmode=driving`;
};

/** Apple Maps multi-stop directions (repeated daddr, iOS 16+ / macOS). Up to 15 stops. */
export const appleRouteUrl = (start, stops) => {
  const enc = (p) => `${p.lat},${p.lng}`;
  const s = stops.slice(0, 15);
  return `https://maps.apple.com/?${start ? `saddr=${enc(start)}&` : ""}${s.map((p) => `daddr=${enc(p)}`).join("&")}&dirflg=d`;
};

export const MAPS_KEY = "ss_maps_app";
export const isAppleDevice = () => /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
export const getMapsApp = () => localStorage.getItem(MAPS_KEY) || (isAppleDevice() ? "apple" : "google");
export const setMapsApp = (v) => localStorage.setItem(MAPS_KEY, v);
export const routeUrl = (app, start, stops) => (app === "apple" ? appleRouteUrl(start, stops) : googleRouteUrl(start, stops));
export const routeStopLimit = (app) => (app === "apple" ? 15 : 10);
export const singleStopUrl = (app, addr) => {
  const q = encodeURIComponent(addr || "");
  return app === "apple" ? `https://maps.apple.com/?daddr=${q}&dirflg=d` : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
};
